<script setup lang="ts">
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverPortal,
  PopoverContent,
} from 'reka-ui';

const props = withDefaults(
  defineProps<{
    erid?: string | null;
    company?: string | null;
    /* Сторона и выравнивание попапа относительно значка */
    side?: 'top' | 'right' | 'bottom' | 'left';
    align?: 'start' | 'center' | 'end';
    /** Цвет круглой подложки значка. */
    triggerColor?: string;
    /** Цвет точек и линий крестика внутри значка. */
    triggerIconColor?: string;
    /*
      copy — согласованный вид с кнопкой копирования токена (мероприятия),
      info — просто текст маркировки в три строки, без кнопки.
    */
    variant?: 'copy' | 'info';
  }>(),
  {
    erid: null,
    company: null,
    inn: null,
    side: 'bottom',
    align: 'end',
    triggerColor: undefined,
    triggerIconColor: 'white',
    variant: 'copy',
  },
);

const isOpen = defineModel<boolean>('open', { default: false });
const isCopied = ref(false);

const isInfo = computed(() => props.variant === 'info');

/* В info-режиме erid может быть еще не выдан — показываем то, что есть */
const hasContent = computed(() =>
  isInfo.value ? Boolean(props.erid || props.company) : Boolean(props.erid),
);

async function copyToken() {
  if (!props.erid) {
    return;
  }
  try {
    await navigator.clipboard.writeText(props.erid);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = props.erid;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
  isCopied.value = true;
  setTimeout(() => {
    isCopied.value = false;
  }, 2000);
}
</script>

<template>
  <PopoverRoot v-if="hasContent" v-model:open="isOpen">
    <PopoverTrigger as-child>
      <button
        :class="$style.trigger"
        :style="{ color: props.triggerColor }"
        @click.prevent.stop
      >
        <!-- dots -->
        <svg
          v-if="!isOpen"
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="10.5" cy="10.5" r="10.5" fill="currentColor" />
          <circle cx="10.5" cy="5.5" r="1.5" :fill="props.triggerIconColor" />
          <circle cx="10.5" cy="10.5" r="1.5" :fill="props.triggerIconColor" />
          <circle cx="10.5" cy="15.5" r="1.5" :fill="props.triggerIconColor" />
        </svg>
        <!-- cross -->
        <svg
          v-else
          width="21"
          height="21"
          viewBox="0 0 21 21"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="10.5" cy="10.5" r="10.5" fill="currentColor" />
          <path
            d="M7 7L14 14M14 7L7 14"
            :stroke="props.triggerIconColor"
            stroke-width="1.2"
            stroke-linecap="round"
          />
        </svg>
      </button>
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        :class="[$style.content, { [$style.content_info!]: isInfo }]"
        :side-offset="4"
        :arrow-padding="0"
        :side="props.side"
        :align="props.align"
        :avoid-collisions="false"
        @click.stop
      >
        <template v-if="isInfo">
          <p v-if="company" :class="$style.infoRow" v-html="company" />
          <p v-if="erid" :class="$style.infoRow">{{ erid }}</p>
        </template>

        <template v-else>
          <p v-if="company" :class="$style.advertiser">{{ company }}</p>

          <button :class="$style.copyBtn" @click="copyToken">
            {{ isCopied ? 'Скопировано' : 'Скопировать токен' }}
          </button>
        </template>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<style module lang="scss">
.trigger {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  padding: 0;
  border: none;
  background: none;
  color: rgba(0, 0, 0, 0.18);
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(0, 0, 0, 0.3);
  }

  &[data-state='open'] {
    color: rgba(0, 0, 0, 0.5);
  }
}

.content {
  display: flex;
  flex-direction: column;
  width: 179px;
  padding: 9px 8px 8px;
  background-color: rgba(255, 255, 255, 0.78);
  border: 1px solid var(--color-gray-line);
  border-radius: 6px;
  z-index: 100;
  transform-origin: var(--reka-popover-content-transform-origin);
  animation: fadeIn 150ms ease-out;

  &::before,
  &::after {
    display: none !important;
  }
}

/*
  Вариант без кнопки: только текст маркировки. Подложку делаем непрозрачной,
  а текст — темным, иначе на цветной/темной картинке читается плохо.
*/
.content_info {
  width: auto;
  min-width: 179px;
  max-width: 260px;
  padding: var(--space-1x) var(--space-1-5x);
  background-color: var(--color-white);
  gap: 2px;
}

.infoRow {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-black-1);
  word-break: break-word;
}

.advertiser {
  margin: 0;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.4;
  letter-spacing: 1px;
  color: var(--color-gray-text);
}

.copyBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  margin-top: 10px;
  padding: 7px 20px;
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-line);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: var(--color-black-1);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background-color: var(--color-gray-bg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
